import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import airbrake from 'helpers/airbrake';
import { isInlineToolCall } from './inline';
import { staticTools } from './static';

import type { AssistantToolCall, StaticToolHandler, ToolCall } from './types';

// Inline tools are rendered by the message that produced them, so the queue only ever holds the
// ones that interrupt the user.
function getQueuedCalls(calls: ToolCall[]) {
  return calls.filter((call): call is AssistantToolCall => !isInlineToolCall(call));
}

interface ToolsContextValue {
  toolCall: AssistantToolCall | null;
  setToolCalls: (calls?: ToolCall[]) => void;
  closeTool: () => void;
  registerStandaloneTool: (name: AssistantToolCall['name']) => () => void;
}

const ToolsContext = createContext<ToolsContextValue | null>(null);

export function useTools() {
  const context = useContext(ToolsContext);
  if (!context) {
    throw new Error('useTools must be used within a ToolsProvider');
  }

  return context;
}

export function ToolsProvider({ children }: { children: React.ReactNode }) {
  const [toolCall, setToolCall] = useState<AssistantToolCall | null>(null);

  const queue = useRef<AssistantToolCall[]>([]);

  const standaloneNames = useRef(new Set<string>());
  const registerStandaloneTool = useCallback((name: string) => {
    standaloneNames.current.add(name);
    return () => {
      standaloneNames.current.delete(name);
    };
  }, []);

  const value = useMemo(() => {
    // Run static tools in order until an interactive one is hit; surface it and hold the rest
    // until the user finishes (closeTool), then resume from where we paused.
    function processQueue(calls: AssistantToolCall[]) {
      for (let i = 0; i < calls.length; i++) {
        const call = calls[i];
        const staticTool = staticTools[call.name] as StaticToolHandler | undefined;

        if (staticTool) {
          staticTool(call);
        } else if (standaloneNames.current.has(call.name)) {
          queue.current = calls.slice(i + 1);
          setToolCall(call);
          return;
        } else {
          airbrake.notify(new Error(`No handler for assistant tool "${call.name}"`));
        }
      }

      queue.current = [];
      setToolCall(null);
    }

    return {
      toolCall,
      registerStandaloneTool,
      setToolCalls: (calls: ToolCall[] = []) => processQueue(getQueuedCalls(calls)),
      closeTool: () => processQueue(queue.current),
    };
  }, [toolCall, registerStandaloneTool]);

  return <ToolsContext.Provider value={value}>{children}</ToolsContext.Provider>;
}

// Register standalone tools via this hook for runtime verification
// NOTE: I'm not confident in this approach - don't be afraid to revisit if this becomes a pain point
export function useStandaloneTool<Name extends AssistantToolCall['name']>(name: Name) {
  const { registerStandaloneTool, toolCall, closeTool } = useTools();

  useEffect(() => registerStandaloneTool(name), [registerStandaloneTool, name]);

  const call = (toolCall?.name === name ? toolCall : null) as Extract<AssistantToolCall, { name: Name }> | null;
  return { call, closeTool };
}
